// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getMyProfile, updateMyProfile, uploadMyProfileImage, updateMyPassword } from '../services/api';
import { Camera, User, Lock, Loader2 } from 'lucide-react';
import Loader from '../components/common/Loader';

const Profile: React.FC = () => {
    const [profile, setProfile] = useState({
        name: '',
        department: '',
        email: '',
        phone: '',
        profile_image: null
    });

    const [passwordData, setPasswordData] = useState({
        new_password: '',
        confirm_password: ''
    });

    const [isLoading, setIsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const data = await getMyProfile();
            setProfile(data);
            if (data.profile_image) {
                setImagePreview(data.profile_image);
            }
        } catch (error) {
            toast.error('Failed to load profile');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGeneralSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Only name and phone are editable; email and department are read-only.
            await updateMyProfile({
                name: profile.name,
                phone: profile.phone,
            });
            // Keep the cached name in sync and tell the sidebar to refresh live.
            localStorage.setItem('user_name', profile.name || '');
            window.dispatchEvent(new Event('user-profile-updated'));
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setSavingPassword(true);
        try {
            await updateMyPassword(passwordData);
            toast.success('Password updated successfully');
            setPasswordData({ new_password: '', confirm_password: '' });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update password');
        } finally {
            setSavingPassword(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const formData = new FormData();
            formData.append('file', file);
            await uploadMyProfileImage(formData);
            await fetchProfile();
            toast.success('Profile image updated');
        } catch (error) {
            toast.error('Failed to upload image');
        }
    };

    const inputClass = 'w-full px-3.5 py-2.5 bg-white border border-line rounded-lg text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-ink transition-all';
    // Standalone (not `${inputClass} …`) so bg-white doesn't win the Tailwind
    // conflict — read-only fields (email, department) must LOOK disabled.
    const disabledInputClass = 'w-full px-3.5 py-2.5 bg-canvas-inset border border-line rounded-lg text-sm text-ink-dim cursor-not-allowed placeholder:text-ink-soft focus:outline-none';

    if (isLoading) {
        return (
            <div className="h-full min-h-[400px] relative">
                <Loader fullPage={false} text="Loading profile..." variant="pulse" overlay={true} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Your account</p>
                <p className="text-sm text-ink-dim mt-1">Your details and how you sign in.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Image Card */}
                <div className="bg-white rounded-card border border-line overflow-hidden">
                    <div className="px-6 py-4 border-b border-line-soft flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                            <Camera size={16} className="text-brand-500" />
                        </div>
                        <h2 className="text-sm font-semibold text-ink">Profile Image</h2>
                    </div>
                    <div className="p-6 flex flex-col items-center">
                        <div className="relative w-28 h-28 mb-3">
                            {imagePreview ? (
                                <img
                                    src={imagePreview.startsWith('http')
                                        ? imagePreview
                                        : `${process.env.REACT_APP_API_URL}/${imagePreview}`}
                                    alt="Profile"
                                    className="w-full h-full rounded-full object-cover border-2 border-line-soft"
                                    onError={() => setImagePreview(null)}
                                />
                            ) : (
                                <div className="w-full h-full rounded-full bg-brand-50 flex items-center justify-center border-2 border-brand-100">
                                    <User size={32} className="text-brand-400" />
                                </div>
                            )}
                            <label className="absolute bottom-0 right-0 w-8 h-8 bg-black text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-ink transition-colors shadow-sm">
                                <Camera size={14} />
                                <input type="file" className="hidden" onChange={handleImageUpload} accept="image/*" />
                            </label>
                        </div>
                        <p className="text-xs text-ink-soft">Click camera to upload</p>
                    </div>
                </div>

                {/* General Information Card */}
                <div className="lg:col-span-2 bg-white rounded-card border border-line overflow-hidden">
                    <div className="px-6 py-4 border-b border-line-soft flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                            <User size={16} className="text-brand-500" />
                        </div>
                        <h2 className="text-sm font-semibold text-ink">General Information</h2>
                    </div>
                    <form onSubmit={handleGeneralSubmit} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">Full Name</label>
                                <input
                                    type="text"
                                    value={profile.name}
                                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                                    className={inputClass}
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">Department</label>
                                <input
                                    type="text"
                                    value={profile.department || ''}
                                    readOnly
                                    disabled
                                    className={disabledInputClass}
                                    placeholder="Not assigned"
                                />
                                <p className="mt-1 text-xs text-ink-soft">Set during onboarding — contact an admin to change it.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
                                <input
                                    type="email"
                                    value={profile.email}
                                    readOnly
                                    disabled
                                    className={disabledInputClass}
                                    placeholder="you@company.com"
                                />
                                <p className="mt-1 text-xs text-ink-soft">Your sign-in email — contact an admin to change it.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-ink mb-1.5">Phone</label>
                                <input
                                    type="tel"
                                    value={profile.phone}
                                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                                    className={inputClass}
                                    placeholder="+1 (555) 000-0000"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end mt-6">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-ink transition-colors disabled:opacity-50 whitespace-nowrap"
                            >
                                {saving && <Loader2 size={16} className="animate-spin" />}
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Security Card */}
            <div className="bg-white rounded-card border border-line overflow-hidden">
                <div className="px-6 py-4 border-b border-line-soft flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-canvas-inset flex items-center justify-center">
                        <Lock size={16} className="text-ink-dim" />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-ink">Security</h2>
                        <p className="text-xs text-ink-dim">Update your password</p>
                    </div>
                </div>
                <form onSubmit={handlePasswordSubmit} className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1.5">New Password</label>
                            <input
                                type="password"
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                                className={inputClass}
                                placeholder="Enter new password"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-ink mb-1.5">Confirm Password</label>
                            <input
                                type="password"
                                value={passwordData.confirm_password}
                                onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                                className={inputClass}
                                placeholder="Confirm new password"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end mt-6">
                        <button
                            type="submit"
                            disabled={savingPassword}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-ink transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            {savingPassword && <Loader2 size={16} className="animate-spin" />}
                            Update Password
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Profile;
